import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  BrainCircuit, Send, Lightbulb, AlertTriangle, CheckCircle,
  Info, Loader2, ChevronDown, ChevronUp, Sparkles
} from "lucide-react";

export default function AIAssist({ game, team }) {
  const { t } = useTranslation();
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [advice, setAdvice] = useState(null);
  const [tips, setTips] = useState([]);
  const [tipsLoading, setTipsLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const suggestedQuestions = [
    t("aiAdvisor.q1"),
    t("aiAdvisor.q2"),
    t("aiAdvisor.q3"),
    t("aiAdvisor.q4"),
    t("aiAdvisor.q5"),
    t("aiAdvisor.q6"),
  ];

  const buildContext = () => {
    const parts = [
      `Game: ${game?.name}, Quarter: ${game?.current_quarter} of ${game?.quarters_count}`,
      `Airline: ${team?.name} (${team?.airline_code})`,
      `Cash: $${(team?.cash || 0).toLocaleString()}`,
      `Aircraft: ${team?.aircraft_count || 0}`,
      `Active Routes: ${(team?.active_routes || []).join(', ')}`,
      `Stock Price: $${(team?.stock_price || 20).toFixed(2)}`,
      `Shares Outstanding: ${(team?.shares_outstanding || 1000000).toLocaleString()}`,
      `Cumulative Profit: $${(team?.cumulative_profit || 0).toLocaleString()}`,
      `Quality Rating: ${(team?.quality_rating || 58).toFixed(1)}/100`,
      `Reliability: ${(team?.reliability || 72).toFixed(1)}/100`,
      `Brand Awareness: ${(team?.brand_awareness || 35).toFixed(1)}/100`,
      `Customer Satisfaction: ${(team?.customer_satisfaction || 55).toFixed(1)}/100`,
      `Employee Satisfaction: ${(team?.employee_satisfaction || 60).toFixed(1)}/100`,
      `Market Share: ${(team?.market_share || 8).toFixed(1)}%`,
      `Short-Term Debt: $${(team?.short_term_debt || 0).toLocaleString()}`,
      `Long-Term Debt: $${(team?.long_term_debt || 0).toLocaleString()}`,
      `Pilots: ${team?.pilots || 12}, Cabin Crew: ${team?.cabin_crew || 12}, Ground Staff: ${team?.ground_staff || 10}`,
    ];
    return parts.join("\n");
  };

  const handleAsk = async (q) => {
    const questionText = q || question;
    if (!questionText.trim()) return;

    setLoading(true);
    setAdvice(null);

    try {
      const res = await axios.post(`${API}/ai/copilot`, {
        context: buildContext(),
        question: questionText,
        team_id: team?.id,
      });
      setAdvice(res.data);
      setHistory((prev) => [
        { question: questionText, advice: res.data, time: new Date().toLocaleTimeString() },
        ...prev,
      ]);
      setQuestion("");
    } catch {
      toast.error(t("aiAdvisor.unavailable"));
    } finally {
      setLoading(false);
    }
  };

  const fetchTips = async () => {
    if (!team?.id) return;
    setTipsLoading(true);
    try {
      const res = await axios.get(`${API}/ai/quick-tips/${team.id}`);
      setTips(res.data.tips || []);
    } catch {
      // Tips unavailable — non-critical
    } finally {
      setTipsLoading(false);
    }
  };

  const tipIcon = (type) => {
    if (type === "warning") return <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />;
    if (type === "success") return <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />;
    return <Info className="w-4 h-4 text-sky-500 shrink-0" />;
  };

  const confidenceColor = (c) => {
    if (c === "high") return "text-emerald-600 bg-emerald-50 border-emerald-200";
    if (c === "low") return "text-rose-600 bg-rose-50 border-rose-200";
    return "text-amber-600 bg-amber-50 border-amber-200";
  };

  return (
    <div className="space-y-6" data-testid="ai-assist-tab">
      {/* Quick Tips */}
      <Card>
        <CardHeader className="card-header-flight-deck flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-400" />
            {t("aiAdvisor.quickTips")}
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={fetchTips}
            disabled={tipsLoading}
            className="border-slate-300"
            data-testid="refresh-tips-btn"
          >
            {tipsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            <span className="ml-2">{tipsLoading ? t("common.loading") : t("aiAdvisor.getTips")}</span>
          </Button>
        </CardHeader>
        <CardContent className="pt-4">
          {tips.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">
              {t("aiAdvisor.tipsDescription")}
            </p>
          ) : (
            <div className="space-y-3">
              {tips.map((tip) => (
                <div
                  key={tip.tip}
                  className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100"
                  data-testid={`tip-item-${tip.type}`}
                >
                  {tipIcon(tip.type)}
                  <p className="text-sm text-slate-700">{tip.tip}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Co-Pilot Chat */}
      <Card>
        <CardHeader className="card-header-flight-deck">
          <CardTitle className="flex items-center gap-2">
            <BrainCircuit className="w-5 h-5 text-sky-400" />
            {t("aiAdvisor.strategicAdvisor")}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          {/* Suggested questions */}
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">{t("aiAdvisor.suggestedQuestions")}</p>
            <div className="flex flex-wrap gap-2">
              {suggestedQuestions.map((sq) => (
                <button
                  key={sq}
                  onClick={() => { setQuestion(sq); handleAsk(sq); }}
                  disabled={loading}
                  className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-sky-50 hover:border-sky-300 hover:text-sky-700 transition-colors disabled:opacity-50"
                  data-testid={`suggested-q-${sq.slice(0, 20)}`}
                >
                  {sq}
                </button>
              ))}
            </div>
          </div>

          {/* Input */}
          <div className="flex gap-2">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAsk()}
              placeholder={t("aiAdvisor.askPlaceholder")}
              className="flex-1 px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              disabled={loading}
              data-testid="ai-question-input"
            />
            <Button
              onClick={() => handleAsk()}
              disabled={loading || !question.trim()}
              className="gap-2"
              data-testid="ai-ask-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {t("aiAdvisor.ask")}
            </Button>
          </div>

          {/* Response */}
          {loading && (
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-100" data-testid="ai-loading">
              <Loader2 className="w-5 h-5 text-sky-500 animate-spin" />
              <p className="text-sm text-slate-600">{t("aiAdvisor.analyzing")}</p>
            </div>
          )}

          {advice && !loading && (
            <div className="space-y-3 p-4 bg-gradient-to-br from-sky-50 to-slate-50 rounded-lg border border-sky-100" data-testid="ai-response">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BrainCircuit className="w-4 h-4 text-sky-600" />
                  <span className="text-sm font-semibold text-slate-800">{t("aiAdvisor.strategicAdvice")}</span>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full border font-medium ${confidenceColor(advice.confidence)}`}>
                  {advice.confidence} {t("aiAdvisor.confidence")}
                </span>
              </div>

              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{advice.advice}</p>

              {advice.key_factors?.length > 0 && (
                <div className="pt-2 border-t border-sky-100">
                  <p className="text-xs font-medium text-slate-500 mb-2">{t("aiAdvisor.keyFactors")}</p>
                  <div className="flex flex-wrap gap-2">
                    {advice.key_factors.map((f) => (
                      <span key={f} className="text-xs px-2 py-1 bg-white rounded border border-slate-200 text-slate-600">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* History */}
          {history.length > 1 && (
            <div>
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
                data-testid="toggle-history-btn"
              >
                {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {t("aiAdvisor.previousQuestions")} ({history.length - 1})
              </button>
              {showHistory && (
                <div className="mt-2 space-y-2 max-h-64 overflow-y-auto">
                  {history.slice(1).map((h) => (
                    <div key={h.time} className="p-3 bg-slate-50 rounded-lg border border-slate-100 text-xs">
                      <div className="flex justify-between mb-1">
                        <span className="font-medium text-slate-700">Q: {h.question}</span>
                        <span className="text-slate-400">{h.time}</span>
                      </div>
                      <p className="text-slate-600 line-clamp-2">{h.advice.advice}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
